import * as THREE from 'three';
import store from '../../../../../redux/store';
import {
  update_assets_data,
  lightDataUpdate,
  selected_object,
  addLightToOtherObject,
  removeIntersectedObject,
  setPushIntersectedObject,
} from '../../../../../redux/Slice/gridSlice';
import { setSelectedLightId } from '../../../../../redux/Slice/globalStates';
import { selectObjectInScene } from '../../../../../pages/Dashboard/GridPage';
import { setSelectedParticle } from '../../../../../redux/Slice/particleSlice';
import { canEstablishParentChild } from '../../../../../utils/hotspotHierarchyUtils';
import { sceneScaleToPanelScale } from '../../../../../components/gridpagefunction/Text.js';

/**
 * For text objects, return the panel-space z (= scene_z / baseZ).
 * For all other object types, return the raw scene z.
 * This prevents z-scale inflation when parent and child texts both have
 * tiny base z (0.000001): the ratio scene_z/baseZ = 1, not an inflated number.
 */
const getSerializedScaleZ = (obj) => {
  if (obj?.deleteType === 'text') {
    return sceneScaleToPanelScale(obj, obj.scale).z;
  }
  return obj.scale.z;
};

const radToDeg = (rad) => (Number(rad) * 180) / Math.PI;

export const useDragDropUtils = ({
  scene,
  assets_data,
  light_data,
  particles,
  dispatch,
  setSelectedObjectState,
  objectt,
  reconstructParentChildRelationships,
  setHierarchyUpdateTrigger,
  forceUpdate, // Add forceUpdate function for immediate UI refresh
}) => {
  const getAssetId = (asset) => asset?._id || asset?.url || asset?.name;

  const getObjectByAsset = (asset) => {
    const id = getAssetId(asset);
    if (!id) return null;
    let obj = scene?.getObjectByName(id);
    if (!obj && asset?.asset_file_name) {
      obj = scene?.getObjectByName(asset.asset_file_name);
    }
    return obj;
  };

  const isAncestor = (maybeAncestor, child) => {
    if (!maybeAncestor || !child) return false;
    let current = child.parent;
    while (current) {
      if (current === maybeAncestor) return true;
      current = current.parent;
    }
    return false;
  };

  const handleDropOnAsset = async (targetAsset, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const dataStr = e.dataTransfer.getData('application/json');
      if (!dataStr) return;

      const data = JSON.parse(dataStr);
      const childId = data?.id || data?.name;
      const targetId =
        getAssetId(targetAsset) || targetAsset._id || targetAsset.name;

      if (!childId || !targetId || childId === targetId) return;

      let childObj = scene.getObjectByName(childId);
      let parentObj = scene.getObjectByName(targetId);

      // Handle special cases for camera
      if (!childObj && data.type === 'camera') {
        childObj =
          scene.getObjectByName('DemoCamera') ||
          scene.children.find(
            (child) =>
              child.type === 'PerspectiveCamera' ||
              child.type === 'OrthographicCamera'
          );
        if (!childObj) {
          childObj = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
          childObj.name = 'DemoCamera';
          scene.add(childObj);
        }
      }

      // Handle particle objects
      if (!childObj && data.type === 'particle') {
        scene.traverse((child) => {
          if (
            child.name === childId ||
            child.userData?.particleId === childId
          ) {
            childObj = child;
          }
        });
      }

      // Handle text objects - search by multiple identifiers
      if (!childObj && data.type === 'text') {
        scene.traverse((child) => {
          if (
            !childObj &&
            (child.name === childId ||
              child._id === childId ||
              child.url === childId ||
              child.deleteType === 'text' &&
              (child.userData?.text_material?.id === childId ||
                child.userData?.id === childId))
          ) {
            childObj = child;
          }
        });
      }

      // Handle hotspot objects (may be nested under another object)
      if (!childObj && data.type === 'hotspot') {
        childObj = scene.getObjectByName(childId);
        if (!childObj) {
          scene.traverse((child) => {
            if (
              (child.userData?.isHotspot || child.deleteType === 'hotspot') &&
              (child.name === childId ||
                child._id === childId ||
                child.url === childId)
            ) {
              childObj = child;
            }
          });
        }
      }

      // Handle GLB objects - search more thoroughly
      if (!childObj && data.type === 'mesh') {
        // First try direct lookup
        childObj = scene.getObjectByName(childId);


        // If not found, search the entire scene hierarchy
        if (!childObj) {
          scene.traverse((child) => {
            if (
              child.name === childId ||
              child._id === childId ||
              child.userData?.id === childId ||
              child.userData?.glbId === childId ||
              (child.isGroup && child.children.some((c) => c.name === childId))
            ) {
              childObj = child;
            }
          });
        }

        // Additional GLB-specific search if still not found
        if (!childObj && data.isGLB) {
          scene.traverse((child) => {
            if (
              child.isGroup &&
              (child.userData?.glbId === data.glbId ||
                child.userData?.originalGLBId === data.glbId ||
                child.children.some((c) => c.userData?.glbId === data.glbId))
            ) {
              childObj = child;
            }
          });
        }
      }

      // Handle camera as drop target
      if (
        !parentObj &&
        (targetId === 'Perspective' ||
          targetAsset.name === 'Camera' ||
          targetId === 'DemoCamera')
      ) {
        try {
          objectt?.showCameraHelper?.(true);
          parentObj =
            scene.getObjectByName('DemoCamera') ||
            scene.children.find(
              (child) =>
                child.type === 'PerspectiveCamera' ||
                child.type === 'OrthographicCamera'
            );
        } catch (err) {
          console.error('Error in camera helper:', err);
        }
      }

      // Handle light as drop target
      if (
        !parentObj &&
        (targetAsset.light_type ||
          targetAsset.type === 'light' ||
          (light_data && light_data.some((l) => l.id === targetId)))
      ) {
        parentObj = scene.getObjectByName(targetId);
        if (!parentObj) {
          scene.traverse((child) => {
            if (
              child.isLight &&
              (child.name === targetId ||
                child.name === targetAsset.name ||
                child.light_type === targetAsset.light_type ||
                child.id === targetId ||
                child.userData?.lightId === targetId)
            ) {
              parentObj = child;
            }
          });
        }
      }

      // Handle text as drop target - search more thoroughly
      if (!parentObj && targetAsset.type === 'text') {
        // First try direct lookup
        parentObj = scene.getObjectByName(targetId);

        // If not found, search the entire scene hierarchy for text objects
        if (!parentObj) {
          scene.traverse((child) => {
            if (
              !parentObj &&
              (child.name === targetId ||
                child._id === targetId ||
                child.url === targetId ||
                (child.deleteType === 'text' &&
                  (child.userData?.text_material?.id === targetId ||
                    child.userData?.id === targetId)))
            ) {
              parentObj = child;
            }
          });
        }
      }

      // Handle GLB as drop target - search more thoroughly
      if (!parentObj && targetAsset.type === 'mesh') {
        // First try direct lookup
        parentObj = scene.getObjectByName(targetId);

        // If not found, search the entire scene hierarchy
        if (!parentObj) {
          scene.traverse((child) => {
            if (
              child.name === targetId ||
              child._id === targetId ||
              child.userData?.id === targetId ||
              child.userData?.glbId === targetId ||
              (child.isGroup && child.children.some((c) => c.name === targetId))
            ) {
              parentObj = child;
            }
          });
        }

        // Additional GLB-specific search if still not found
        if (!parentObj) {
          scene.traverse((child) => {
            if (
              child.isGroup &&
              (child.userData?.glbId === targetId ||
                child.userData?.originalGLBId === targetId ||
                child.children.some((c) => c.userData?.glbId === targetId))
            ) {
              parentObj = child;
            }
          });
        }
      }

      if (!childObj || !parentObj || isAncestor(childObj, parentObj)) {
        console.warn('GLB Drag Drop Failed:', {
          childObj: childObj ? childObj.name : 'NOT FOUND',
          parentObj: parentObj ? parentObj.name : 'NOT FOUND',
          isAncestor: isAncestor(childObj, parentObj),
        });
        return;
      }

      if (!canEstablishParentChild(parentObj, childObj)) {
        return;
      }

      // Update allIntersectedObjects - remove child from top-level if it was there
      dispatch(removeIntersectedObject(childObj));

      // Use attach to preserve world transform and automatically handle removal from old parent
      try {
        parentObj.attach(childObj);
      } catch (addError) {
        if (!parentObj.children) parentObj.children = [];
        if (!Object.isExtensible(parentObj.children)) parentObj.children = [...parentObj.children];
        parentObj.children.push(childObj);
        childObj.parent = parentObj;
        childObj.updateMatrixWorld(true);
      }

      // Add child back to allIntersectedObjects so it can still be selected
      // This ensures child objects remain selectable even when they're nested
      dispatch(setPushIntersectedObject(childObj));

      // Update Redux state based on type
      const parentId =
        getAssetId(targetAsset) || targetAsset.name || targetAsset._id;

      // Update Redux state based on type

      // Create a promise that resolves when Redux state is updated
      const updateReduxStatePromise = new Promise((resolve) => {
        if (
          data.type &&
          ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].includes(
            data.type
          )
        ) {
          const updatedAssets = assets_data[data.type]?.map((asset) => {
            const assetId = getAssetId(asset);
            if (assetId === childId) {
              return {
                ...asset,
                parent: parentId,
                position_x: childObj.position.x,
                position_y: childObj.position.y,
                position_z: childObj.position.z,
                rotation_x: radToDeg(childObj.rotation.x),
                rotation_y: radToDeg(childObj.rotation.y),
                rotation_z: radToDeg(childObj.rotation.z),
                scale_x: data.type === 'image'
                  ? (childObj.geometry?.parameters?.width || 1) * childObj.scale.x
                  : childObj.scale.x,
                scale_y: data.type === 'image'
                  ? (childObj.geometry?.parameters?.height || 1) * childObj.scale.y
                  : childObj.scale.y,
                scale_z: getSerializedScaleZ(childObj),
              };
            }
            return asset;
          });
          if (updatedAssets) {
            dispatch(
              update_assets_data({ type: data.type, data: updatedAssets })
            );
          }
        } else if (data.type === 'camera') {
          // Handle camera parent updates - cameras don't typically have parent relationships in the same way
          // but we can still update their position relative to parent if needed
        } else if (data.type === 'light') {
          const lightObj = scene.getObjectByName(childId);
          if (lightObj && lightObj.parent && lightObj.parent !== parentObj) {
            lightObj.parent.remove(lightObj);
          }
          const updatedLights = light_data.map((light) => {
            if (light.id === childId || light._id === childId) {
              return {
                ...light,
                parent: parentId,
                position_x: childObj.position.x,
                position_y: childObj.position.y,
                position_z: childObj.position.z,
                rotation_x: radToDeg(childObj.rotation.x),
                rotation_y: radToDeg(childObj.rotation.y),
                rotation_z: radToDeg(childObj.rotation.z),
                scale_x: childObj.scale.x,
                scale_y: childObj.scale.y,
                scale_z: getSerializedScaleZ(childObj),
              };
            }
            return light;
          });
          dispatch(lightDataUpdate(updatedLights));
          dispatch(addLightToOtherObject({ id: data.id }));
        } else if (data.type === 'particle') {
          // ✅ Only dispatch once, correctly
          dispatch({
            type: 'particles/updateParticle',
            payload: {
              id: childId,
              updates: {
                parent: parentId,
                position_x: childObj.position.x,
                position_y: childObj.position.y,
                position_z: childObj.position.z,
                rotation_x: radToDeg(childObj.rotation.x),
                rotation_y: radToDeg(childObj.rotation.y),
                rotation_z: radToDeg(childObj.rotation.z),
                scale_x: childObj.scale.x,
                scale_y: childObj.scale.y,
                scale_z: getSerializedScaleZ(childObj),
              },
            },
          });
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
        // Use multiple requestAnimationFrame calls to ensure Redux state is processed
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
      });

      // Wait for Redux state update to complete
      await updateReduxStatePromise;

      // Verify that the Redux state was actually updated

      const currentState = store.getState();
      const currentAssetsData = currentState.assets_object.assets_data;

      if (
        data.type &&
        ['image', 'mesh', 'video', 'audio', 'text'].includes(data.type)
      ) {
        const updatedAsset = currentAssetsData[data.type]?.find(
          (asset) => getAssetId(asset) === childId
        );
        if (updatedAsset) {
          // Asset successfully verified in store
        } else {
          console.warn('Asset not found in updated Redux state!');
        }
      }

      // Force immediate hierarchy update after Redux state changes
      if (setHierarchyUpdateTrigger) {
        setHierarchyUpdateTrigger((prev) => prev + 1);
      }

      dispatch(selected_object(parentObj));
      selectObjectInScene(parentObj.name || parentObj.url || parentObj._id);

      // Force immediate UI update using the provided forceUpdate function
      if (forceUpdate) {
        forceUpdate();
      }

      // Special handling for GLB objects - trigger mesh extraction update
      if (data.type === 'mesh') {

        // Force GLB mesh extraction update for both child and parent
        if (forceUpdate) {
          // Trigger immediate UI update to refresh GLB mesh lists
          forceUpdate();
        }
      }

      // Trigger reconstruction after successful drop to update hierarchy
      // Use multiple requestAnimationFrame calls to ensure Redux state is fully processed
      if (reconstructParentChildRelationships) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const currentState = store.getState();
            const latestAssetsData = currentState.assets_object.assets_data;
            const latestLightData = currentState.assets_object.light_data;
            const latestParticles = currentState.particles?.particles || [];

            // Only reconstruct non-particle assets to avoid overwriting particle hierarchy
            reconstructParentChildRelationships(
              scene,
              latestAssetsData,
              latestLightData,
              null,
              latestParticles // ← pass latest particles so it has correct state
            );
          });
        });
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const handleDropToRoot = async (e) => {
    e.preventDefault();

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const childId = data?.id || data?.name;
      if (!childId) return;

      let childObj = scene.getObjectByName(childId);


      // Handle camera resolution similar to handleDropOnAsset
      if (!childObj && data.type === 'camera') {
        childObj =
          scene.getObjectByName('DemoCamera') ||
          scene.children.find(
            (child) =>
              child.type === 'PerspectiveCamera' ||
              child.type === 'OrthographicCamera'
          );
        if (!childObj) {
          childObj = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
          childObj.name = 'DemoCamera';
          scene.add(childObj);
        }
      }

      // Handle particles
      if (!childObj && data.type === 'particle') {
        scene.traverse((child) => {
          if (
            child.name === childId ||
            child.userData?.particleId === childId
          ) {
            childObj = child;
          }
        });
      }

      // Handle text objects - search by multiple identifiers
      if (!childObj && data.type === 'text') {
        scene.traverse((child) => {
          if (
            !childObj &&
            (child.name === childId ||
              child._id === childId ||
              child.url === childId ||
              (child.deleteType === 'text' &&
                (child.userData?.text_material?.id === childId ||
                  child.userData?.id === childId)))
          ) {
            childObj = child;
          }
        });
      }

      // Handle lights
      if (!childObj && data.type === 'light') {
        scene.traverse((child) => {
          if (
            child.isLight &&
            (child.name === childId ||
              child.id === childId ||
              child.userData?.lightId === childId)
          ) {
            childObj = child;
          }
        });
      }

      // Handle GLB/mesh resolution
      if (!childObj && data.type === 'mesh') {
        // direct lookup first
        childObj = scene.getObjectByName(childId);

        // deep search by various identifiers
        if (!childObj) {
          scene.traverse((child) => {
            if (
              child.name === childId ||
              child._id === childId ||
              child.userData?.id === childId ||
              child.userData?.glbId === childId ||
              (child.isGroup && child.children.some((c) => c.name === childId))
            ) {
              childObj = child;
            }
          });
        }
      }

      if (!childObj) return;

      // Note: We DO NOT call childObj.parent.remove(childObj) here.
      // scene.attach() needs the object to still be in its old parent to correctly calculate its world transform.

      // Update allIntersectedObjects - remove child from top-level if it was there
      dispatch(removeIntersectedObject(childObj));

      scene.attach(childObj); // move to root, preserve world transform

      // Sync Redux: clear parent for this object based on its type
      const updateReduxStatePromise = new Promise((resolve) => {
        if (
          data.type &&
          ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].includes(
            data.type
          )
        ) {
          const updatedAssets = assets_data[data.type]?.map((asset) => {
            const assetId = asset._id || asset?.url || asset?.name;
            if (assetId === childId) {
              return {
                ...asset,
                parent: null,
                position_x: childObj.position.x,
                position_y: childObj.position.y,
                position_z: childObj.position.z,
                rotation_x: radToDeg(childObj.rotation.x),
                rotation_y: radToDeg(childObj.rotation.y),
                rotation_z: radToDeg(childObj.rotation.z),
                scale_x: data.type === 'image'
                  ? (childObj.geometry?.parameters?.width || 1) * childObj.scale.x
                  : childObj.scale.x,
                scale_y: data.type === 'image'
                  ? (childObj.geometry?.parameters?.height || 1) * childObj.scale.y
                  : childObj.scale.y,
                scale_z: getSerializedScaleZ(childObj),
              };
            }
            return asset;
          });
          if (updatedAssets) {
            dispatch(
              update_assets_data({ type: data.type, data: updatedAssets })
            );
          }
        } else if (data.type === 'light') {
          const updatedLights = light_data.map((light) => {
            if (light.id === childId || light._id === childId) {
              return {
                ...light,
                parent: null,
                position_x: childObj.position.x,
                position_y: childObj.position.y,
                position_z: childObj.position.z,
                rotation_x: radToDeg(childObj.rotation.x),
                rotation_y: radToDeg(childObj.rotation.y),
                rotation_z: radToDeg(childObj.rotation.z),
                scale_x: childObj.scale.x,
                scale_y: childObj.scale.y,
                scale_z: getSerializedScaleZ(childObj),
              };
            }
            return light;
          });
          dispatch(lightDataUpdate(updatedLights));
        } else if (data.type === 'particle') {
          dispatch({
            type: 'particles/updateParticle',
            payload: {
              id: childId,
              updates: {
                parent: null,
                position_x: childObj.position.x,
                position_y: childObj.position.y,
                position_z: childObj.position.z,
                rotation_x: radToDeg(childObj.rotation.x),
                rotation_y: radToDeg(childObj.rotation.y),
                rotation_z: radToDeg(childObj.rotation.z),
                scale_x: childObj.scale.x,
                scale_y: childObj.scale.y,
                scale_z: getSerializedScaleZ(childObj),
              },
            },
          });
        }

        // allow reducers to flush
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

      await updateReduxStatePromise;

      // Add child back to allIntersectedObjects so it can still be selected
      dispatch(setPushIntersectedObject(childObj));

      // Handle selection based on type
      if (data.type === 'camera') {
        store.dispatch(selected_object(childObj));
        selectObjectInScene('DemoCamera');
      } else if (data.type === 'light') {
        store.dispatch(selected_object(childObj));
        selectObjectInScene(childId);
        dispatch(setSelectedLightId(childId));
      } else if (data.type === 'particle') {
        store.dispatch(selected_object(childObj));
        selectObjectInScene(childId);
        dispatch(setSelectedParticle(childId));
      } else {
        store.dispatch(selected_object(childObj));
        selectObjectInScene(childId);
      }

      // Use multiple requestAnimationFrame calls to ensure Redux state is processed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // trigger hierarchy update counter if provided
          if (setHierarchyUpdateTrigger) {
            setHierarchyUpdateTrigger((prev) => prev + 1);
          }

          // Force immediate UI update using the provided forceUpdate function
          if (forceUpdate) {
            forceUpdate();
          }

          // Trigger reconstruction after successful drop to root to update hierarchy
          if (reconstructParentChildRelationships) {
            const currentState = store.getState();
            const latestAssetsData = currentState.assets_object.assets_data;
            const latestLightData = currentState.assets_object.light_data;
            const latestParticles = currentState.particles?.particles || [];

            reconstructParentChildRelationships(
              scene,
              latestAssetsData,
              latestLightData,
              null,
              latestParticles // ← pass latest particles
            );
          }
        });
      });
    } catch (err) {
      console.error('Drop to root error:', err);
    }
  };

  return {
    handleDropOnAsset,
    handleDropToRoot,
    getAssetId,
    getObjectByAsset,
    isAncestor,
  };
};
